<script setup>
import { computed, h } from 'vue';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import wootConstants from 'dashboard/constants/globals';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import { useImpersonation } from 'dashboard/composables/useImpersonation';

import {
  DropdownContainer,
  DropdownBody,
  DropdownSection,
  DropdownItem,
} from 'next/dropdown-menu/base';
import Button from 'next/button/Button.vue';

const { t } = useI18n();
const store = useStore();
const currentUser = useMapGetter('getCurrentUser');
const currentUserAvailability = useMapGetter('getCurrentUserAvailability');
const currentAccountId = useMapGetter('getCurrentAccountId');

const { isImpersonating } = useImpersonation();

const { AVAILABILITY_STATUS_KEYS } = wootConstants;
const statusList = computed(() => {
  return [
    t('PROFILE_SETTINGS.FORM.AVAILABILITY.STATUS.ONLINE'),
    t('PROFILE_SETTINGS.FORM.AVAILABILITY.STATUS.BUSY'),
    t('PROFILE_SETTINGS.FORM.AVAILABILITY.STATUS.OFFLINE'),
  ];
});

const statusColors = ['bg-n-teal-9', 'bg-n-amber-9', 'bg-n-slate-9'];

const availabilityStatuses = computed(() => {
  return statusList.value.map((statusLabel, index) => ({
    label: statusLabel,
    value: AVAILABILITY_STATUS_KEYS[index],
    color: statusColors[index],
    icon: h('span', {
      class: [statusColors[index], 'size-[12px] rounded-full'],
    }),
    active: currentUserAvailability.value === AVAILABILITY_STATUS_KEYS[index],
  }));
});

const activeStatus = computed(() => {
  return availabilityStatuses.value.find(status => status.active);
});

function changeAvailabilityStatus(availability) {
  if (isImpersonating.value) {
    useAlert(t('PROFILE_SETTINGS.FORM.AVAILABILITY.IMPERSONATING_ERROR'));
    return;
  }
  try {
    store.dispatch('updateAvailability', {
      availability,
      account_id: currentAccountId.value,
    });
  } catch (error) {
    useAlert(t('PROFILE_SETTINGS.FORM.AVAILABILITY.SET_AVAILABILITY_ERROR'));
  }
}
</script>

<template>
  <DropdownSection class="[&>ul]:overflow-visible">
    <DropdownItem preserve-open>
      <div class="min-w-0 flex flex-1 flex-col">
        <div class="truncate text-base font-semibold text-n-slate-12">
          {{ currentUser.available_name }}
        </div>
        <div class="truncate text-xs text-n-slate-10">
          {{ currentUser.email }}
        </div>
      </div>
      <DropdownContainer>
        <template #trigger="{ toggle }">
          <Button
            size="sm"
            color="slate"
            variant="faded"
            class="min-w-[96px]"
            icon="i-lucide-chevron-down"
            trailing-icon
            @click="toggle"
          >
            <div class="flex gap-1 items-center flex-grow text-sm">
              <div class="p-1 flex-shrink-0">
                <div class="size-2 rounded-full" :class="activeStatus.color" />
              </div>
              <span>{{ activeStatus.label }}</span>
            </div>
          </Button>
        </template>
        <DropdownBody class="min-w-32 z-20">
          <DropdownItem
            v-for="status in availabilityStatuses"
            :key="status.value"
            :label="status.label"
            :icon="status.icon"
            class="cursor-pointer"
            @click="changeAvailabilityStatus(status.value)"
          />
        </DropdownBody>
      </DropdownContainer>
    </DropdownItem>
  </DropdownSection>
</template>
